import React, { useState, useEffect } from "react";
import { Row, Col, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import axios from "axios";

const PhysiotherapyTab = ({
  emrRecordId,
  emrRecord,
  emrRecordLocal,
  setEmrRecordLocal,
}) => {
  const [formData, setFormData] = useState({
    joint: [],
    muscles: [],
    treatment: [],
    position: [],
    therapy: [],
    total_hjhs_scoring: null,
    total_fish_scoring: null,
    comments: null,
  });

  useEffect(() => {
    let userToken = localStorage.getItem("token");
    const config = {
      method: "GET",
      url: `${process.env.REACT_APP_API_URL}/physiotherapy/record/${emrRecordId}`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios(config)
      .then(function (response) {
        // console.log("this is response", JSON.stringify(response.data));
        if (response.data.physiotherapy) {
          const scoreData =
            response.data.physiotherapy.physiotherapy_score || {};
          setFormData({
            joint: Array.isArray(scoreData.joint) ? scoreData.joint : [],
            muscles: Array.isArray(scoreData.muscles) ? scoreData.muscles : [],
            treatment: Array.isArray(scoreData.treatment)
              ? scoreData.treatment
              : [],
            position: Array.isArray(scoreData.position)
              ? scoreData.position
              : [],
            therapy: Array.isArray(scoreData.therapy) ? scoreData.therapy : [],
            total_hjhs_scoring: scoreData.total_hjhs_scoring || null,
            total_fish_scoring: scoreData.total_fish_scoring || null,
            comments: scoreData.comments || null,
          });
        }
      })
      .catch(function (error) {
        if (error.response?.data?.message) {
          toast.error(JSON.stringify(error.response.data.message));
        }
        console.log(JSON.stringify(error.response?.data));
      });
  }, []);

  // Helper function to get labels from array of values
  const getLabelsFromValues = (values, options) => {
    if (!Array.isArray(values) || values.length === 0) return [];
    return values
      .map((value) => {
        const option = options.find((opt) => opt.value === value);
        return option ? option.label : value;
      })
      .filter(Boolean);
  };

  // Helper component to display array values as badges
  const DisplayArrayField = ({
    values,
    options,
    emptyMessage = "Not specified",
  }) => {
    const labels = getLabelsFromValues(values, options);
    if (labels.length === 0) {
      return (
        <div
          style={{ color: "#6c757d", fontStyle: "italic", padding: "8px 0" }}
        >
          {emptyMessage}
        </div>
      );
    }
    return (
      <div
        style={{
          padding: "8px 0",
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
        }}
      >
        {labels.map((label, index) => (
          <Badge
            key={index}
            bg="primary"
            style={{
              fontSize: "0.875rem",
              padding: "6px 12px",
              borderRadius: "4px",
            }}
          >
            {label}
          </Badge>
        ))}
      </div>
    );
  };

  // Dropdown options - customize these based on your requirements
  const jointOptions = [
    { value: "right_shoulder", label: "RIGHT SHOULDER" },
    { value: "right_elbow", label: "RIGHT ELBOW" },
    { value: "right_wrist", label: "RIGHT WRIST" },
    { value: "right_four_arm", label: "RIGHT FOUR ARM" },
    { value: "right_hip", label: "RIGHT HIP" },
    { value: "right_knee", label: "RIGHT KNEE" },
    { value: "right_ankle", label: "RIGHT ANKLE" },
    { value: "left_shoulder", label: "LEFT SHOULDER" },
    { value: "left_elbow", label: "LEFT ELBOW" },
    { value: "left_wrist", label: "LEFT WRIST" },
    { value: "left_four_arm", label: "LEFT FOUR ARM" },
    { value: "left_hip", label: "LEFT HIP" },
    { value: "left_knee", label: "LEFT KNEE" },
    { value: "left_ankle", label: "LEFT ANKLE" },
  ];

  const musclesOptions = [
    { value: "deltoids_muscles", label: "DELTOIDS MUSCLES" },
    { value: "trapezius_muscles", label: "TRAPEZIUS MUSCLES" },
    { value: "rhomboid_minor_muscles", label: "RHOMBOID MINOR MUSCLES" },
    { value: "rhomboid_major_muscles", label: "RHOMBOID MAJOR MUSCLES" },
    { value: "supraspinatus_muscles", label: "SUPRASPINATUS MUSCLES" },
    { value: "infraspinatus_muscles", label: "INFRASPINATUS MUSCLES" },
    { value: "subscapularis_muscles", label: "SUBSCAPULARIS MUSCLES" },
    { value: "biceps_brachii", label: "BICEPS BRACHII" },
    { value: "brachialis", label: "BRACHIALIS" },
    { value: "brachioradialis", label: "BRACHIORADIALIS" },
    { value: "triceps_brachii", label: "TRICEPS BRACHII" },
    { value: "anconeus_extensors", label: "ANCONEUS(EXTENSORS)" },
    { value: "pronators", label: "PRONATORS" },
    { value: "supinators", label: "SUPINATORS" },
    { value: "hip_flexors", label: "HIP FLEXORS" },
    { value: "hip_extensors", label: "HIP EXTENSORS" },
    { value: "hip_abductors", label: "HIP ABDUCTORS" },
    { value: "hip_adductors", label: "HIP ADDUCTORS" },
    { value: "hip_rotators", label: "HIP ROTATORS" },
    { value: "hamstrings", label: "HAMSTRINGS" },
    { value: "quadriceps", label: "QUADRICEPS" },
    { value: "calf_muscles", label: "CALF MUSCLES" },
  ];

  const treatmentOptions = [
    {
      value: "upperlimb_basic_activity_mv_shoulder",
      label: "UPPERLIMB BASIC ACTIVILY M.V SHOULDER",
    },
    {
      value: "upperlimb_basic_activity_mv_elbow",
      label: "UPPERLIMB BASIC ACTIVILY M.V ELBOW",
    },
    {
      value: "upperlimb_basic_activity_mv_four_arm",
      label: "UPPERLIMB BASIC ACTIVILY M.V FOUR ARM",
    },
    {
      value: "upperlimb_basic_activity_mv_wrist",
      label: "UPPERLIMB BASIC ACTIVILY M.V WRIST",
    },
    {
      value: "lowerlimb_basic_activity_mv_hip",
      label: "LOWERLIMB BASIC ACTIVILY M.V HIP",
    },
    {
      value: "lowerlimb_basic_activity_mv_knee",
      label: "LOWERLIMB BASIC ACTIVILY M.V KNEE",
    },
    {
      value: "lowerlimb_basic_activity_mv_ankle",
      label: "LOWERLIMB BASIC ACTIVILY M.V ANKLE",
    },
    {
      value: "strengthening_exercises_ul_shoulder",
      label: "STRENGTHENING EXCERCISES FOR U.L OF SHOULDER",
    },
    {
      value: "strengthening_exercises_ul_elbow",
      label: "STRENGTHENING EXCERCISES FOR U.L OF ELBOW",
    },
    {
      value: "strengthening_exercises_ul_four_arm",
      label: "STRENGTHENING EXCERCISES FOR U.L OF FOUR ARM",
    },
    {
      value: "strengthening_exercises_ul_wrist",
      label: "STRENGTHENING EXCERCISES FOR U.L OF WRIST",
    },
    {
      value: "strengthening_exercises_ll_hip",
      label: "STRENGTHENING EXCERCISES FOR L.L OF HIP",
    },
    {
      value: "strengthening_exercises_ll_knee",
      label: "STRENGTHENING EXCERCISES FOR L.L OF KNEE",
    },
    {
      value: "strengthening_exercises_ll_ankle",
      label: "STRENGTHENING EXCERCISES FOR L.L OF ANKLE",
    },
    {
      value: "upper_body_stretches_exercise",
      label: "UPPER BODY STRETCHES EXERCISE",
    },
    {
      value: "lower_body_stretches_exercise",
      label: "LOWER BODY STRETCHES EXERCISE",
    },
    {
      value: "neck_body_stretches_exercise",
      label: "NECK BODY STRETCHES EXERCISE",
    },
  ];

  const positionOptions = [
    { value: "sitting", label: "SITTING" },
    { value: "supine", label: "SUPINE" },
    { value: "proneline", label: "PRONELINE" },
    { value: "standing", label: "STANDING" },
  ];

  const therapyOptions = [
    { value: "physiotherapy", label: "PHYSIOTHERAPY" },
    { value: "price", label: "PRICE" },
  ];

  return (
    <div style={{ padding: "20px" }}>
      {/* Row 1: Joint, Muscles, Position */}
      <Row className="mb-4">
        <Col xs={12} md={4} className="mb-3 mb-md-0">
          <div className="newPatientItem w-100">
            <label
              htmlFor="joint"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Joint
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
              }}
            >
              <DisplayArrayField
                values={formData.joint}
                options={jointOptions}
                emptyMessage="No joints specified"
              />
            </div>
          </div>
        </Col>
        <Col xs={12} md={4} className="mb-3 mb-md-0">
          <div className="newPatientItem w-100">
            <label
              htmlFor="muscles"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Muscles
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
              }}
            >
              <DisplayArrayField
                values={formData.muscles}
                options={musclesOptions}
                emptyMessage="No muscles specified"
              />
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="newPatientItem w-100">
            <label
              htmlFor="position"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Position
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
              }}
            >
              <DisplayArrayField
                values={formData.position}
                options={positionOptions}
                emptyMessage="No positions specified"
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Row 2: Treatment, Therapy */}
      <Row className="mb-4">
        <Col xs={12} md={6} className="mb-3">
          <div className="newPatientItem w-100">
            <label
              htmlFor="treatment"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Treatment
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
              }}
            >
              <DisplayArrayField
                values={formData.treatment}
                options={treatmentOptions}
                emptyMessage="No treatments specified"
              />
            </div>
          </div>
        </Col>
        <Col xs={12} md={6} className="mb-3">
          <div className="newPatientItem w-100">
            <label
              htmlFor="therapy"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Therapy
            </label>
            <div
              style={{
                minHeight: "40px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
              }}
            >
              <DisplayArrayField
                values={formData.therapy}
                options={therapyOptions}
                emptyMessage="No therapy specified"
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Row 3: HJHS Scoring, FISH Scoring */}
      <Row className="mb-4">
        <Col xs={12} sm={6} className="mb-3 mb-md-0">
          <div className="newPatientItem w-100">
            <label
              htmlFor="total_hjhs_scoring"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Total HJHS Scoring
            </label>
            <div
              style={{
                height: "38px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                display: "flex",
                alignItems: "center",
                color: formData.total_hjhs_scoring ? "#333" : "#6c757d",
                fontStyle: formData.total_hjhs_scoring ? "normal" : "italic",
              }}
            >
              {formData.total_hjhs_scoring !== null &&
              formData.total_hjhs_scoring !== undefined
                ? formData.total_hjhs_scoring
                : "Not specified"}
            </div>
          </div>
        </Col>
        <Col xs={12} sm={6}>
          <div className="newPatientItem w-100">
            <label
              htmlFor="total_fish_scoring"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Total FISH Scoring
            </label>
            <div
              style={{
                height: "38px",
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                display: "flex",
                alignItems: "center",
                color: formData.total_fish_scoring ? "#333" : "#6c757d",
                fontStyle: formData.total_fish_scoring ? "normal" : "italic",
              }}
            >
              {formData.total_fish_scoring !== null &&
              formData.total_fish_scoring !== undefined
                ? formData.total_fish_scoring
                : "Not specified"}
            </div>
          </div>
        </Col>
      </Row>

      {/* Row 4: Comments */}
      <Row>
        <Col xs={12}>
          <div className="newPatientItem w-100">
            <label
              htmlFor="comments"
              style={{
                marginBottom: "8px",
                fontWeight: "600",
                fontSize: "0.95rem",
                color: "#333",
              }}
            >
              Comments
            </label>
            <div
              style={{
                minHeight: "100px",
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "4px",
                border: "1px solid #dee2e6",
                whiteSpace: "pre-wrap",
                color: formData.comments ? "#333" : "#6c757d",
                fontStyle: formData.comments ? "normal" : "italic",
              }}
            >
              {formData.comments || "No comments"}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default PhysiotherapyTab;
